import React from "react";
import {
  SelectionGroup,
  SelectionItem,
} from "../../../Components/Selection/Selection";
import { LightIcon } from "../../../AssetComponents/Images";
import { RentalItem } from "../../../Components/RealEstateProperty/RentalItem";
import { useSelector, useDispatch } from "react-redux";
import { PropertyImageWithFallback } from "../../../Components/Images/PropertyImageWithFallback";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Fade } from "react-awesome-reveal";
import NoTripsyet from "../../../Components/DashBoardSvg/NoTripsyet";
import { getMyReservations } from "../../../Components/functions/Functions";

export const Reservations = () => {
  const dispatch = useDispatch();
  const account = useSelector((state) => state.auth.account);
  const diffToUTC = useSelector((state) => state.time.diffToUTC);
  const location = useLocation();
  const navigate = useNavigate();
  const [flag, setFlag] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [backendReservations, setBackendReservations] = useState([]);
  const setPeriodParams = (token_id, from, to) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("from", from);
    searchParams.set("to", to);
    navigate(location.pathname + `/${token_id}?${searchParams.toString()}`);
  };

  // 🚀 OPTIMIZED: Load reservations from backend API (pre-filtered by status)
  // Memoize loadReservations to prevent unnecessary re-creations
  const loadReservations = useCallback(async (skipLoading = false) => {
    if (!account) {
      console.log("⏭️ [RESERVATIONS] Skipping loadReservations - no account");
      return;
    }
    
    console.log(`🔄 [RESERVATIONS] loadReservations called (skipLoading: ${skipLoading}, flag: ${flag})`);
    
    if (!skipLoading) {
      setIsLoading(true);
    }
    
    try {
      // Map flag to status: 0=upcoming, 1=current, 2=completed, 3=cancelled
      const statusMap = ["upcoming", "current", "completed", "cancelled"];
      const status = statusMap[flag] || "all";
      
      console.log(`📡 [RESERVATIONS] Fetching reservations for status: ${status}`);
      // 🚀 OPTIMIZED: Use optimized backend API (pre-filtered, ~200-500ms vs 20-50s)
      const reservations = await getMyReservations(account, status, diffToUTC, dispatch);
      
      console.log(`📥 [RESERVATIONS] Received ${reservations?.length || 0} reservations`);
      console.log(`📊 [RESERVATIONS] Reservation details:`, reservations?.map(r => ({
        token_id: r.nft?.token_id,
        traveler: r.reservation?.address,
        approved: r.reservation?.approved,
        cancelled: r.reservation?.cancelled,
        period: r.reservation?.renting_period,
      })));
      
      setBackendReservations(reservations || []);
      setIsLoading(false);
      console.log("✅ [RESERVATIONS] Reservations loaded and state updated");
    } catch (error) {
      console.error("❌ [RESERVATIONS] Error loading reservations:", error);
      console.error("❌ [RESERVATIONS] Error details:", error.message, error.stack);
      setIsLoading(false);
    }
  }, [account, flag, diffToUTC, dispatch]);

  useEffect(() => {
    loadReservations();
  }, [loadReservations, refreshTrigger]);

  // 🚀 INSTANT DASHBOARDS: Use backend data only (no fallback client-side filtering)
  const matchingReservations = useMemo(() => {
    // Backend already filters by status, so just return the backend data
    return backendReservations || [];
  }, [backendReservations]);

  // 🚀 INSTANT DASHBOARDS: Listen for socket-based reservation updates (real-time, no window events)
  useEffect(() => {
    // Get socket from Redux or context if available, otherwise use window socket
    // For now, we'll use a custom event that App.js will emit when socket events arrive
    const handleReservationUpdate = (event) => {
      const detail = event?.detail;
      
      // Optimistic update: immediately remove the affected reservation
      // from local state so UI updates before API call returns
      if (detail?.immediate && detail?.token_id) {
        setBackendReservations(prev => 
          prev.filter(item => item.nft?.token_id !== detail.token_id)
        );
      }
      
      console.log("🔄 [RESERVATIONS] Reservation update detected via socket, refreshing list...", {
        eventType: event.type,
        detail: event?.detail,
      });
      // Then fetch fresh data from backend with staggered retries to handle DB lag
      loadReservations(true);
      // Retry 1: catch DB write lag (500ms)
      setTimeout(() => {
        loadReservations(true);
      }, 500);
      // Retry 2: final catch for slow DB writes (1500ms)
      loadReservations(true);
      setTimeout(() => {
        loadReservations(true);
        setRefreshTrigger(prev => prev + 1);
      }, 1500);
    };

    // Listen for socket-based reservation events (emitted by App.js from socket.io)
    window.addEventListener('reservationStatusUpdate', handleReservationUpdate);
    window.addEventListener('reservation:pending.v2', handleReservationUpdate);
    window.addEventListener('reservation:confirmed', handleReservationUpdate);
    window.addEventListener('reservation:updated', handleReservationUpdate);
    window.addEventListener('reservation:approved', handleReservationUpdate);
    window.addEventListener('reservation:cancelled', handleReservationUpdate);
    window.addEventListener('reservation:rejected', handleReservationUpdate);
    window.addEventListener('reservation:finalized', handleReservationUpdate);
    window.addEventListener('reservation:forceRefresh', handleReservationUpdate);
    window.addEventListener('reservation:refreshList', handleReservationUpdate);
    
    // Backward compatibility with old custom events (will be removed in future)
    window.addEventListener('reservationConfirmed', handleReservationUpdate);
    window.addEventListener('reservationUpdated', handleReservationUpdate);
    window.addEventListener('reservationApproved', handleReservationUpdate);
    window.addEventListener('reservationCancelled', handleReservationUpdate);
    window.addEventListener('reservationFinalized', handleReservationUpdate);
    window.addEventListener('reservationRejected', handleReservationUpdate);
    window.addEventListener('reservationCreated', handleReservationUpdate);

    return () => {
      window.removeEventListener('reservationStatusUpdate', handleReservationUpdate);
      window.removeEventListener('reservation:pending.v2', handleReservationUpdate);
      window.removeEventListener('reservation:confirmed', handleReservationUpdate);
      window.removeEventListener('reservation:updated', handleReservationUpdate);
      window.removeEventListener('reservation:approved', handleReservationUpdate);
      window.removeEventListener('reservation:cancelled', handleReservationUpdate);
      window.removeEventListener('reservation:rejected', handleReservationUpdate);
      window.removeEventListener('reservation:finalized', handleReservationUpdate);
      window.removeEventListener('reservation:forceRefresh', handleReservationUpdate);
      window.removeEventListener('reservation:refreshList', handleReservationUpdate);
      window.removeEventListener('reservationConfirmed', handleReservationUpdate);
      window.removeEventListener('reservationUpdated', handleReservationUpdate);
      window.removeEventListener('reservationApproved', handleReservationUpdate);
      window.removeEventListener('reservationCancelled', handleReservationUpdate);
      window.removeEventListener('reservationFinalized', handleReservationUpdate);
      window.removeEventListener('reservationRejected', handleReservationUpdate);
      window.removeEventListener('reservationCreated', handleReservationUpdate);
    };
  }, [loadReservations]);

  return (
    <div className="w-full overflow-auto max-h-[calc(100vh-80px)] h-full p-[6px] sm:p-[10px]">
      <div className="w-full h-full space-y-[12px] sm:space-y-[20px] rounded-[10px] p-[4px] sm:p-[8px] flex flex-col">
        {/* Header Section */}
        <div className="bg-white border-[2px] h-max border-[#E3E3E3] p-[16px] sm:p-[24px] rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
          <div className="flex items-center gap-[8px] sm:gap-[10px]">
            <div className="p-[6px] sm:p-[8px] w-[28px] h-[28px] sm:w-[32px] sm:h-[32px] flex items-center justify-center rounded-full bg-[#5b1dee]">
              <svg
                width="12"
                height="12"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="scale-75 sm:scale-100"
              >
                <g filter="url(#filter0_d_1108_71791)">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.00023 12.2521C6.64237 11.5359 5.91006 11.0839 5.10945 11.0851H2.62306C2.39099 11.0851 2.16843 10.9929 2.00433 10.8288C1.84023 10.6647 1.74805 10.4421 1.74805 10.21V3.20631C1.74805 2.97424 1.84023 2.75168 2.00433 2.58758C2.16843 2.42349 2.39099 2.3313 2.62306 2.3313H4.66616C5.2852 2.3313 5.87888 2.57721 6.3166 3.01493C6.75432 3.45266 7.00023 4.04634 7.00023 4.66537C7.00023 3.3763 8.04523 2.3313 9.33431 2.3313H11.3774C11.6095 2.3313 11.832 2.42349 11.9961 2.58758C12.1602 2.75168 12.2524 2.97424 12.2524 3.20631V10.21C12.2524 10.6925 11.8619 11.0839 11.3795 11.0851H8.89102C8.09041 11.0839 7.3581 11.5359 7.00023 12.2521Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.00023 12.2521C6.64237 11.5359 5.91006 11.0839 5.10945 11.0851H2.62306C2.39099 11.0851 2.16843 10.9929 2.00433 10.8288C1.84023 10.6647 1.74805 10.4421 1.74805 10.21V3.20631C1.74805 2.97424 1.84023 2.75168 2.00433 2.58758C2.16843 2.42349 2.39099 2.3313 2.62306 2.3313H4.66616C5.2852 2.3313 5.87888 2.57721 6.3166 3.01493C6.75432 3.45266 7.00023 4.04634 7.00023 4.66537C7.00023 3.3763 8.04523 2.3313 9.33431 2.3313H11.3774C11.6095 2.3313 11.832 2.42349 11.9961 2.58758C12.1602 2.75168 12.2524 2.97424 12.2524 3.20631V10.21C12.2524 10.6925 11.8619 11.0839 11.3795 11.0851H8.89102C8.09041 11.0839 7.3581 11.5359 7.00023 12.2521Z"
                    stroke="#202020"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
                <path
                  d="M7.00077 4.66553V12.0234"
                  stroke="#202020"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.5 4.95756H5.25073"
                  stroke="#202020"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.5 7.00004H5.25073"
                  stroke="#202020"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.5 9.04252H5.25073"
                  stroke="#202020"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.75195 4.95756H10.5027"
                  stroke="#202020"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.75195 7.00004H10.5027"
                  stroke="#202020"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.75195 9.04252H10.5027"
                  stroke="#202020"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <defs>
                  <filter
                    id="filter0_d_1108_71791"
                    x="1.24805"
                    y="1.8313"
                    width="12.5039"
                    height="11.9209"
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                  >
                    <feFlood floodOpacity="0" result="BackgroundImageFix" />
                    <feColorMatrix
                      in="SourceAlpha"
                      type="matrix"
                      values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                      result="hardAlpha"
                    />
                    <feOffset dx="1" dy="1" />
                    <feComposite in2="hardAlpha" operator="out" />
                    <feColorMatrix
                      type="matrix"
                      values="0 0 0 0 0.12549 0 0 0 0 0.12549 0 0 0 0 0.12549 0 0 0 1 0"
                    />
                    <feBlend
                      mode="normal"
                      in2="BackgroundImageFix"
                      result="effect1_dropShadow_1108_71791"
                    />
                    <feBlend
                      mode="normal"
                      in="SourceGraphic"
                      in2="effect1_dropShadow_1108_71791"
                      result="shape"
                    />
                  </filter>
                </defs>
              </svg>
            </div>

            <div className="font-semibold text-[20px] sm:text-[24px]">Reservations</div>
          </div>

          <div className="text-[#666666] font-normal text-sm sm:text-base mt-1">
            Your Real Estate Status
          </div>
        </div>

        {/* Controls Section */}
        <div className="w-full px-[10px] sm:px-[20px] flex flex-col sm:flex-row sm:justify-between sm:items-center gap-[12px] sm:gap-[16px]">
          <SelectionGroup
            defaultItem={flag}
            className="border-[2px] border-[#e3e3e3] w-full sm:w-max px-[4px] sm:px-[6px] py-[3px] sm:py-[4px] gap-[4px] sm:gap-[8px] flex items-center rounded-[14px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
          >
            <SelectionItem
              SelectedItem={
                <div className="py-[3px] sm:py-[4px] cursor-pointer bg-white rounded-[10px] w-[80px] sm:w-[120px] flex justify-center gap-[6px] sm:gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <div className="text-black font-semibold text-xs sm:text-base">Upcoming</div>
                  <div className="scale-75 sm:scale-100">
                    <LightIcon />
                  </div>
                </div>
              }
              UnselectedItem={
                <div
                  onClick={() => setFlag(0)}
                  className="py-[3px] sm:py-[4px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-[80px] sm:w-[120px] flex justify-center"
                >
                  <div className="text-[#959595] text-xs sm:text-base">Upcoming</div>
                </div>
              }
            />
            <SelectionItem
              SelectedItem={
                <div className="py-[3px] sm:py-[4px] cursor-pointer bg-white rounded-[10px] w-[80px] sm:w-[120px] flex justify-center gap-[6px] sm:gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <div className="text-black font-semibold text-xs sm:text-base">Current</div>
                  <div className="scale-75 sm:scale-100">
                    <LightIcon />
                  </div>
                </div>
              }
              UnselectedItem={
                <div
                  onClick={() => setFlag(1)}
                  className="py-[3px] sm:py-[4px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-[80px] sm:w-[120px] flex justify-center"
                >
                  <div className="text-[#959595] text-xs sm:text-base">Current</div>
                </div>
              }
            />
            <SelectionItem
              SelectedItem={
                <div className="py-[3px] sm:py-[4px] cursor-pointer bg-white rounded-[10px] w-[80px] sm:w-[120px] flex justify-center gap-[6px] sm:gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <div className="text-black font-semibold text-xs sm:text-base">Completed</div>
                  <div className="scale-75 sm:scale-100">
                    <LightIcon />
                  </div>
                </div>
              }
              UnselectedItem={
                <div
                  onClick={() => setFlag(2)}
                  className="py-[3px] sm:py-[4px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-[80px] sm:w-[120px] flex justify-center"
                >
                  <div className="text-[#959595] text-xs sm:text-base">Completed</div>
                </div>
              }
            />
            <SelectionItem
              SelectedItem={
                <div className="py-[3px] sm:py-[4px] cursor-pointer bg-white rounded-[10px] w-[80px] sm:w-[120px] flex justify-center gap-[6px] sm:gap-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                  <div className="text-black font-semibold text-xs sm:text-base">Cancelled</div>
                  <div className="scale-75 sm:scale-100">
                    <LightIcon />
                  </div>
                </div>
              }
              UnselectedItem={
                <div
                  onClick={() => setFlag(3)}
                  className="py-[3px] sm:py-[4px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-[80px] sm:w-[120px] flex justify-center"
                >
                  <div className="text-[#959595] text-xs sm:text-base">Cancelled</div>
                </div>
              }
            />
          </SelectionGroup>

          {/* Search Bar */}
          <div className="flex gap-[8px] sm:gap-[10px] items-center border-[#E3E3E3] border-[1px] rounded-[12px] pl-[12px] sm:pl-[16px] pr-[6px] sm:pr-[8px] py-[8px] sm:py-[9px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] w-full sm:w-auto sm:min-w-[250px]">
            <svg
              width="12"
              height="12"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="scale-75 sm:scale-100 flex-shrink-0"
            >
              <path
                d="M12.25 12.25L8.75 8.75M1.75 5.83333C1.75 6.36956 1.85562 6.90054 2.06083 7.39596C2.26603 7.89137 2.56681 8.34151 2.94598 8.72069C3.32515 9.09986 3.7753 9.40063 4.27071 9.60584C4.76612 9.81105 5.2971 9.91667 5.83333 9.91667C6.36956 9.91667 6.90054 9.81105 7.39596 9.60584C7.89137 9.40063 8.34151 9.09986 8.72069 8.72069C9.09986 8.34151 9.40063 7.89137 9.60584 7.39596C9.81105 6.90054 9.91667 6.36956 9.91667 5.83333C9.91667 5.2971 9.81105 4.76612 9.60584 4.27071C9.40063 3.7753 9.09986 3.32515 8.72069 2.94598C8.34151 2.56681 7.89137 2.26603 7.39596 2.06083C6.90054 1.85562 6.36956 1.75 5.83333 1.75C5.2971 1.75 4.76612 1.85562 4.27071 2.06083C3.7753 2.26603 3.32515 2.56681 2.94598 2.94598C2.56681 3.32515 2.26603 3.7753 2.06083 4.27071C1.85562 4.76612 1.75 5.2971 1.75 5.83333Z"
                stroke="#202020"
                strokeWidth="1.16667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <input
              placeholder="Search"
              className="flex-1 outline-none text-sm sm:text-base min-w-0"
            />
            <svg
              width="18"
              height="18"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="scale-75 sm:scale-100 flex-shrink-0"
            >
              <rect width="20" height="20" rx="4" fill="#E3E3E3" />
              <path
                d="M11.4837 6.38636L9.13991 15.0938H8.19176L10.5355 6.38636H11.4837Z"
                fill="#202020"
              />
            </svg>
          </div>
        </div>

        {/* Reservations Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 px-[10px] sm:px-[20px] lg:px-[40px] gap-[12px] sm:gap-[16px] lg:gap-[20px] w-full max-h-[calc(100vh-300px)] sm:max-h-[calc(100vh-200px)] overflow-auto">
          {isLoading ? (
            <div className="flex flex-col items-center w-full justify-center h-[calc(100vh-400px)] col-span-full">
              <div className="text-sm sm:text-base">Loading reservations...</div>
            </div>
          ) : matchingReservations.length > 0 ? (
            matchingReservations.map(({ nft, reservation, key }) => {
              // 🚀 FIX: Add approval status to key to force re-render when status changes
              const statusKey = `${key}-${reservation?.approved ? 'approved' : 'pending'}-${reservation?.cancelled ? 'cancelled' : 'active'}`;
              return (
                <Fade key={statusKey}>
                  <RentalItem
                    traveler={reservation?.address}
                    rentingPeriod={reservation?.renting_period}
                    token_id={nft?.token_id}
                    hideWishlist={true}
                    onClick={() => {
                      const rentingPeriod = reservation?.renting_period;
                      if (Array.isArray(rentingPeriod) && rentingPeriod.length >= 2 && nft?.token_id) {
                        setPeriodParams(
                          nft.token_id,
                          rentingPeriod[0],
                          rentingPeriod[1]
                        );
                      }
                    }}
                  />
                </Fade>
              );
            })
          ) : (
            <div className="flex flex-col items-center justify-center w-full h-[calc(100vh-300px)] col-span-full">
              <div className="scale-75 sm:scale-100">
                <NoTripsyet />
              </div>
              <div className="text-sm sm:text-base mt-4">No reservations yet</div>
              <div className="text-[#737373] text-xs sm:text-sm text-center mt-2">
                {flag === 0 && "No upcoming reservations yet"}
                {flag === 1 && "No current reservations"}
                {flag === 2 && "No completed reservations"}
                {flag === 3 && "No cancelled reservations"}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
